import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { refusalMessage } from '../api';
import { clientResets, riderResets } from '../api/passwordResets';
import InGoLogo from '../components/InGoLogo';
import { DriverForgotPasswordLayout, IconBack, IconInfo } from './ForgotPasswordPage';
import './auth.css';
import './driverForgotPasswordPremium.css';

/**
 * The page a reset link opens (ingo-e0, 2 Oct 2026): `/reset/:token` for a
 * client, `/driver/reset/:token` for a rider. It asks the server whose account
 * the link opens before asking for anything, so a dead link says it is dead
 * (used, expired, replaced by a newer one) and offers a new one. A new
 * password signs out every other session and never signs anybody in: they
 * sign in with it after.
 */
export default function ResetPasswordPage({ realm = 'customer', resets = realm === 'driver' ? riderResets : clientResets }) {
  const { token = '' } = useParams();
  const isRider = realm === 'driver';
  const loginPath = isRider ? '/driver/login' : '/login';
  const forgotPath = `/forgot-password?realm=${isRider ? 'driver' : 'customer'}`;
  const [link, setLink] = useState(null);
  const [dead, setDead] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [done, setDone] = useState('');

  useEffect(() => {
    let cancelled = false;
    resets.read(token).then(
      (data) => !cancelled && setLink(data),
      (err) => !cancelled && setDead(refusalMessage(err)),
    );

    return () => {
      cancelled = true;
    };
  }, [resets, token]);

  const choose = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (password === '') {
      setErrorMessage('Choose a password.');
      return;
    }
    if (password !== confirmation) {
      setErrorMessage('The two passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      setDone(await resets.choose(token, password, confirmation));
    } catch (err) {
      const said = refusalMessage(err);
      // A link that died while the form was open: say so where the form was.
      if (err?.status === 404 || err?.status === 409 || err?.status === 410) setDead(said);
      else setErrorMessage(said);
    } finally {
      setBusy(false);
    }
  };

  let body;
  if (done) {
    body = (
      <>
        <h2 className={isRider ? 'dp-fp-title' : 'auth-title auth-title--subhead-gap'}>Password changed</h2>
        <p className={isRider ? 'dp-fp-msg dp-fp-msg--success' : 'auth-message auth-message--success'} role="status">
          {done}
        </p>
        <Link to={loginPath} className={isRider ? 'dp-fp-btn-primary dp-fp-btn-primary--link' : 'auth-btn-primary auth-btn-primary--link auth-btn-primary--stacked'}>
          Go to login
        </Link>
      </>
    );
  } else if (dead) {
    body = (
      <>
        <h2 className={isRider ? 'dp-fp-title' : 'auth-title auth-title--subhead-gap'}>This link does not work</h2>
        <p className={isRider ? 'dp-fp-msg dp-fp-msg--error' : 'auth-message auth-message--error'} role="alert">
          {dead}
        </p>
        <Link to={forgotPath} className={isRider ? 'dp-fp-btn-primary dp-fp-btn-primary--link' : 'auth-btn-primary auth-btn-primary--link auth-btn-primary--stacked'}>
          Ask for a new link
        </Link>
      </>
    );
  } else if (!link) {
    body = <p className={isRider ? 'dp-fp-subtitle' : 'auth-subtitle'}>Checking your link…</p>;
  } else {
    body = (
      <>
        <h2 className={isRider ? 'dp-fp-title' : 'auth-title auth-title--subhead-gap'}>Choose a new password</h2>
        <p className={isRider ? 'dp-fp-subtitle' : 'auth-subtitle'}>
          {link.first_name ? `${link.first_name}, choose` : 'Choose'} a new password for {link.email}.
        </p>
        <form onSubmit={choose} noValidate>
          <div className={isRider ? 'dp-fp-field' : 'auth-field'}>
            <label className={isRider ? 'dp-fp-label' : 'auth-label'} htmlFor="reset-password">
              New password
            </label>
            <div className={isRider ? 'dp-fp-input-wrap' : 'auth-input-wrap'}>
              <input
                id="reset-password"
                className={isRider ? 'dp-fp-input' : 'auth-input'}
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={isRider ? { paddingLeft: '0.85rem' } : undefined}
              />
            </div>
          </div>
          <div className={isRider ? 'dp-fp-field' : 'auth-field auth-field--last'}>
            <label className={isRider ? 'dp-fp-label' : 'auth-label'} htmlFor="reset-confirmation">
              Confirm new password
            </label>
            <div className={isRider ? 'dp-fp-input-wrap' : 'auth-input-wrap'}>
              <input
                id="reset-confirmation"
                className={isRider ? 'dp-fp-input' : 'auth-input'}
                type="password"
                autoComplete="new-password"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                style={isRider ? { paddingLeft: '0.85rem' } : undefined}
              />
            </div>
          </div>
          {errorMessage ? (
            <p className={isRider ? 'dp-fp-msg dp-fp-msg--error' : 'auth-message auth-message--error'} role="alert">
              {errorMessage}
            </p>
          ) : null}
          <button type="submit" className={isRider ? 'dp-fp-btn-primary' : 'auth-btn-primary'} disabled={busy}>
            {busy ? 'Please wait…' : 'Set password'}
          </button>
        </form>
        {isRider ? (
          <div className="dp-fp-info" role="note">
            <IconInfo />
            <p className="dp-fp-info-text">Every other phone signed in to your account is signed out.</p>
          </div>
        ) : null}
      </>
    );
  }

  if (isRider) {
    return (
      <DriverForgotPasswordLayout loginPath={loginPath} title="New Password" foot={null}>
        {body}
      </DriverForgotPasswordLayout>
    );
  }

  return (
    <div className="auth-page auth-page--register auth-page--forgot">
      <div className="auth-top">
        <Link to={loginPath} className="auth-back" aria-label="Back to login">
          <IconBack />
        </Link>
      </div>
      <div className="auth-card">
        <InGoLogo variant="auth" />
        {body}
      </div>
    </div>
  );
}
